import { useEffect, useRef, useState } from 'react';
import { Upload, Camera, Eye, FileText, Loader2, X } from 'lucide-react';
import { Badge, Button, Modal } from './UI';

/**
 * Attaches flight tickets to one passenger. Accepts files dropped onto the
 * cell, picked from disk, or photographed with the machine's camera — an
 * organiser at a desk usually has a PDF, one at a venue has a paper ticket.
 * Several at once are fine: an e-ticket, a boarding pass and an invoice all
 * belong to the same leg.
 */
export default function TicketDrop({ person, tickets = [], readOnly = false, onUpload }) {
  const [over, setOver] = useState(false);
  const [busy, setBusy] = useState(false);
  const [camera, setCamera] = useState(false);
  const [viewing, setViewing] = useState(false);
  const fileRef = useRef(null);
  const hasTicket = tickets.length > 0;

  const send = async (files) => {
    const list = Array.from(files || []).filter(Boolean);
    if (!list.length) return;
    setBusy(true);
    try {
      await onUpload(person, list);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div
        onDragOver={(e) => { if (!readOnly) { e.preventDefault(); setOver(true); } }}
        onDragLeave={() => setOver(false)}
        onDrop={(e) => {
          if (readOnly) return;
          e.preventDefault();
          setOver(false);
          send(e.dataTransfer.files);
        }}
        style={{
          display: 'flex', alignItems: 'center', gap: 6,
          padding: '5px 8px', borderRadius: 'var(--r-sm)',
          border: `1px dashed ${over ? 'var(--gold-400)' : 'transparent'}`,
          background: over ? 'rgba(239,212,138,0.09)' : 'transparent',
          transition: 'background 0.15s, border-color 0.15s',
        }}
      >
        <input
          ref={fileRef} type="file" hidden multiple accept="image/*,application/pdf"
          onChange={(e) => { send(e.target.files); e.target.value = ''; }}
        />

        {busy ? (
          <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text-mid)' }}>
            <Loader2 size={13} className="gl-spin" /> Sending
          </span>
        ) : (
          <>
            {hasTicket && (
              <button
                onClick={() => (tickets.length === 1 ? window.open(tickets[0].url, '_blank', 'noopener') : setViewing(true))}
                title={tickets.length === 1 ? 'Open the ticket' : `View ${tickets.length} documents`}
                style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, color: 'var(--gold-300)' }}
              >
                <Eye size={13} />
                View{tickets.length > 1 ? ` (${tickets.length})` : ''}
              </button>
            )}

            {!readOnly && (
              <>
                <button
                  onClick={() => fileRef.current?.click()}
                  title={over ? 'Drop to upload' : 'Upload tickets, or drop them here'}
                  style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, color: 'var(--text-mid)' }}
                >
                  <Upload size={13} />
                  {hasTicket ? 'Replace' : 'Upload'}
                </button>
                <button
                  onClick={() => setCamera(true)}
                  title="Photograph the ticket"
                  style={{ display: 'flex', padding: 2, color: 'var(--text-mid)' }}
                >
                  <Camera size={13} />
                </button>
              </>
            )}

            {!hasTicket && readOnly && (
              <span style={{ fontSize: 12, color: 'var(--text-lo)' }}>—</span>
            )}
          </>
        )}
      </div>

      <Modal
        open={viewing} onClose={() => setViewing(false)}
        title="Travel documents" subtitle={person.name} width={420}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {tickets.map((t) => (
            <a
              key={t.url} href={t.url} target="_blank" rel="noopener noreferrer"
              style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '11px 12px', borderRadius: 'var(--r-sm)',
                fontSize: 13, color: 'var(--text-hi)',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--ink-800)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
            >
              <FileText size={14} color="var(--gold-500)" style={{ flexShrink: 0 }} />
              <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {t.fileName || 'Travel document'}
              </span>
              {t.direction && <Badge tone="neutral">{t.direction}</Badge>}
            </a>
          ))}
        </div>
      </Modal>

      <CameraModal
        open={camera}
        onClose={() => setCamera(false)}
        name={person.name}
        onCapture={(file) => { setCamera(false); send([file]); }}
      />
    </>
  );
}

/** Live camera preview that grabs a still and hands back a JPEG file. */
function CameraModal({ open, onClose, name, onCapture }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;

    navigator.mediaDevices?.getUserMedia({ video: { facingMode: 'environment' } })
      .then((stream) => {
        if (cancelled) { stream.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = stream;
        if (videoRef.current) videoRef.current.srcObject = stream;
      })
      .catch(() => setError('No camera available on this device. Use Upload instead.'));

    return () => {
      cancelled = true;
      streamRef.current?.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    };
  }, [open]);

  const snap = () => {
    const video = videoRef.current;
    if (!video) return;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d').drawImage(video, 0, 0);
    canvas.toBlob((blob) => {
      if (blob) onCapture(new File([blob], `ticket-${Date.now()}.jpg`, { type: 'image/jpeg' }));
    }, 'image/jpeg', 0.9);
  };

  return (
    <Modal
      open={open} onClose={onClose}
      title="Photograph the ticket" subtitle={name} width={460}
      footer={(
        <>
          <Button variant="subtle" icon={X} onClick={onClose}>Cancel</Button>
          <Button icon={Camera} onClick={snap} disabled={Boolean(error)}>Capture</Button>
        </>
      )}
    >
      {error ? (
        <p style={{ fontSize: 13, color: 'var(--warn)', lineHeight: 1.7 }}>{error}</p>
      ) : (
        <video
          ref={videoRef} autoPlay playsInline muted
          style={{ width: '100%', borderRadius: 'var(--r)', background: 'var(--ink-900)', display: 'block' }}
        />
      )}
    </Modal>
  );
}
